import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router, NavigationCancel, NavigationEnd } from '@angular/router';
import { FilterableService } from './components/ve/filterable-service';
import { cgpAuthentication } from '../modules/cgp-authentication';

const MODULES = ['VE_GA', 'VE_Body'];

class ModuleUser {
    GMIN: any;
    ModuleClaims: any[];
    RoleClaims: any[];

    constructor(data: any) {
        if (data) {
            this.GMIN = data.GMIN;
            this.ModuleClaims = data.ModuleClaims;
            this.RoleClaims = data.RoleClaims;
        } else {
            this.reset();
        }
    }

    reset() {
        this.GMIN = null;
        this.ModuleClaims = [];
        this.RoleClaims = [];
    }
}

@Injectable()
export class AuthenticationService {
    private currentUser: any;
    private currentRoute: string;
    private readonly userProfileService: FilterableService = new FilterableService();
    private readonly module: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    constructor(
        private readonly router: Router,
    ) {
        this.userProfileService.subscribe(x => {
            if (!x['ModuleClaims']) {
                return;
            }
            this.currentUser = new ModuleUser(x);
            this.currentRoute = this.router.url;
        });

        const roleClaims = [];
        const userProfile = Object(cgpAuthentication.USER_PROFILE.value);
        roleClaims.push.apply(roleClaims, userProfile.BodyRoles);
        roleClaims.push.apply(roleClaims, userProfile.GARoles);
        userProfile.RoleClaims = roleClaims;
        this.userProfileService.next(userProfile);

        router.events.subscribe((e) => {
            if (e instanceof NavigationCancel && !this.currentUser) {
                this.currentRoute = e.url;
            }
            if (e instanceof NavigationEnd) {
                this.currentRoute = e.url;
            }
        });

        if (userProfile.ModuleClaims.length === 1) {
            this.module.next(userProfile.ModuleClaims[0]);
        } else {
            this.setModule();
        }
    }

    /**
     * Takes an array of claims and validates if the user's claims are valid against the requesting claims.
     * ### Example
     *
     * ```
     * Config Claims:
     * path: 'VE',
     * component: VeComponent,
     * canActivate: [AuthGuard],
     * data: {
     *     navbarId: 've',
     *     expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
     * }
     *
     * Html Claims:
     *  *ngIf="authService?.isClaimValid(['Administrator', 'Manager', 'Lead', 'Engineer', 'User'])"
     * ```
     * @param {*} claims
     * @returns {boolean}
     * @memberof AuthenticationService
     */
    isClaimValid(claims): boolean | undefined {
        if (!this.currentUser) { return false; }

        // Use case of config claims.
        if (claims.data) {
            const expectedRoles = claims.data.expectedRoles.map(x => x.toLowerCase());
            const validClaims = this.currentUser.RoleClaims.filter(x => {
                const userRoleClaims = x.split('_');
                const currentUserModule = this.getModule().split('_');
                if (userRoleClaims.length > 1 && expectedRoles.includes(userRoleClaims[2].toLowerCase()) && currentUserModule[1].toLowerCase() === userRoleClaims[0].toLowerCase()) {
                    return x;
                }
            });
            return validClaims.length > 0;
            // Use case of html claims.
        } else if (claims.length > 0) {
            claims = claims.map(x => x.toLowerCase());
            const validClaims = this.currentUser.RoleClaims.filter(x => {
                const userRoleClaims = x.split('_');
                const currentUserModule = this.getModule().split('_');
                if (userRoleClaims.length > 1 && claims.includes(userRoleClaims[2].toLowerCase()) && currentUserModule[1].toLowerCase() === userRoleClaims[0].toLowerCase()) {
                    return x;
                }
            });
            return validClaims.length > 0;
        }
    }


    /**
     * Takes an array of claims and a status and determines if the workOrderClaim is valid.
     *
     * @param {*} claims
     * @param {*} status
     * @returns {boolean}
     * @memberof AuthenticationService
     */
    isWorkOrderEditClaimValid(claims, status): boolean {
        let isValid = false;
        if (status === 'OnHold' || status === 'In Progress') {
            const user = this.getUser();
            const moduleRoleClaims = [];
            user.RoleClaims.forEach(roleClaim => {
                const userRoleClaims = roleClaim.split('_');
                const currentUserModule = this.getModule().split('_');
                if (currentUserModule[1].toLowerCase() === userRoleClaims[0].toLowerCase()) {
                    moduleRoleClaims.push(roleClaim);
                }
            });
            if (moduleRoleClaims.length > 1) {
                isValid = true;
            } else {
                const isVeUser = user.RoleClaims.filter(roleClaim => {
                    const userRoleClaims = roleClaim.split('_');
                    const currentUserModule = this.getModule().split('_');
                    if (roleClaim.includes('User') && currentUserModule[1].toLowerCase() === userRoleClaims[0].toLowerCase()) {
                        return roleClaim;
                    }
                });
                isValid = isVeUser.length === 0;
            }
        } else {
            isValid = this.isClaimValid(claims);
        }

        if (!isValid) {
            this.router.navigate(['/VE/Work-Orders']);
        }
        return isValid;
    }

    getUserProfile = (): BehaviorSubject<any> => this.userProfileService;

    /**
     *
     */
    getUserGMIN = () => this.currentUser ? this.currentUser.GMIN : null;

    /**
     *
     */
    getModule = (): string => this.module.value;

    getUser = () => this.currentUser ? this.currentUser : null;

    /**
     *
     */
    setModule = () => {
        if (!this.module.value) {
            this.module.next(localStorage.getItem('module'));
            if (!this.module.value) { this.module.next('VE_GA'); }
        }
    }

    switchModule = (url = '') => {
        const currentModule = this.module.value;
        const nextModule = MODULES.filter(x => x !== currentModule);
        this.module.next(nextModule[0]);
        localStorage.setItem('module', this.module.value);
        let currentUrl = window.location.href;
        let n = currentUrl.indexOf('VE/');
        if (n === -1) { window.location.reload(); } else {
            n += 2;
            currentUrl = currentUrl.substring(0, n !== -1 ? n : currentUrl.length);
            if (url !== '') {
                window.location.replace(url);
            } else {
                window.location.replace(currentUrl);
            }
            window.location.reload();
        }
    }
}

